import React, { useEffect, useState } from 'react';

import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useLocation, useRouteMatch } from 'react-router-dom';

import { useQuery } from '@apollo/client';

import routes from 'lane-shared/config/routes';
import { LONG_DATE_TIME } from 'lane-shared/helpers/constants/dates';
import { getClient } from 'lane-shared/apollo';
import { WorkOrderModuleSettings } from 'lane-shared/domains/workOrder';
import {
  INTERACTION_CANCELLED,
  INTERACTION_CLOSED,
} from 'constants-interactions';
import { convertTo62, convertToUUID, safeConvertToUUID } from 'uuid-encoding';
import { dateFormatter } from 'lane-shared/helpers/formatters';
import {
  useFlag,
  useChannelServiceRequestFeaturesQuery,
  useChannelProfileQuery,
  useSetContextualLDAttributes,
} from 'lane-shared/hooks';
import {
  useScheduleServiceRequestDueDateEnabled,
  useWorkOrderTenantSurveysEnabled,
} from 'react-hooks';
import { FeatureFlag } from 'constants-flags';
import {
  AttachmentDocumentContentTypeEnum,
  AttachmentImageContentTypeEnum,
  AttachmentVariantEnum,
  Channel,
  ChannelServiceRequestFeaturesType,
} from 'application-types';
import { ContentTypeEnum } from 'constants-content';
import { AddAttachment, Button, Flex } from 'components';
import { Button as WhiteLabelButton } from 'design-system-web';
import { UserThumbnailWithLoader } from 'components/lane/UserThumbnailWithLoader';
import { ExternalEntityType } from 'lane-shared/domains/billingPayments/types';
import { ServiceRequestStatusEnum } from 'domains/workOrder/serviceRequest/components/types/serviceRequestStatus';
import {
  getMyMessagingTokenQuery,
  getServiceRequestByIdQuery,
  updateServiceRequestStatusByIdMutation,
} from 'graphql-queries';
import { Modal } from 'components/lds';
import { M } from 'components/typography';
import { MemberServiceRequestEdit } from './MemberServiceRequestEdit';
import { StatusChip } from './StatusChip';
import { FeedbackButton } from './FeedbackButton';
import { ServiceRequestFeedbackContainer } from './ServiceRequestFeedbackContainer';
import { useSurveySubmissionEligibility } from './useSurveySubmissionEligibility';

import styles from './MemberServiceRequestDetail.scss';
import { MemberChargeList } from 'domains/billingAndPayments/components/MemberChargeList';
import { AmazonS3Buckets } from 'lane-shared/config';
import { ActiveChannelTypeEnum } from 'constants-channel';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { WorkOrderComments } from 'domains-work-order-comments-web';

import { CustomQuestion } from 'graphql-query-contracts';
import type { GetServiceRequestByIdQueryQuery } from 'graphql-query-contracts';
import { getServiceRequestStatusEnum } from '../../../helpers/getServiceRequestStatusEnum';

type ServiceRequestData = NonNullable<
  GetServiceRequestByIdQueryQuery['serviceRequest']
>;

export interface MemberServiceRequestDetailProps {
  serviceRequestId: string;
  settings: WorkOrderModuleSettings;
  channel: Channel;
  timeZone: string;
}

const EMPTY_VALUE = '-';

const ServiceRequestHeader = ({
  serviceRequest,
  canEdit,
  isEditing,
  setIsEditing,
  onFeedbackSubmit,
  settings,
  setOpenCancelRequestModel,
  timeZone,
}: {
  serviceRequest: ServiceRequestData;
  canEdit: boolean;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  onFeedbackSubmit: () => void;
  settings: WorkOrderModuleSettings;
  setOpenCancelRequestModel: (open: boolean) => void;
  timeZone: string;
}) => {
  const { t } = useTranslation();
  const isTenantSurveysEnabled = useWorkOrderTenantSurveysEnabled();
  const serviceRequestStatus = getServiceRequestStatusEnum(
    serviceRequest.status
  );

  return (
    <div className={styles.headingContainer}>
      <div className={styles.leftColumn}>
        <p className={styles.heading}>{serviceRequest?.category}</p>
        <span className={styles.headingSubtitle}>
          {t`web.member.serviceRequest.created` +
            dateFormatter(
              serviceRequest?.createdAt,
              LONG_DATE_TIME,
              timeZone
            )}
        </span>
      </div>
      <div className={styles.rightColumn}>
        {isTenantSurveysEnabled && serviceRequestStatus && (
          <FeedbackButton
            serviceRequestStatus={serviceRequestStatus}
            serviceRequest={{
              serviceRequestId: serviceRequest.serviceRequestId || '',
              userFriendlyID: serviceRequest?.userFriendlyID || '',
              status: serviceRequest?.status || '',
              category: serviceRequest?.category || '',
              issue: serviceRequest?.issue || '',
            }}
            feedback={serviceRequest?.feedback}
            onFeedbackSubmit={onFeedbackSubmit}
            settings={settings}
          />
        )}
        {canEdit && (
          <Button
            size="medium"
            onClick={() => setIsEditing(!isEditing)}
            testId="edit-button"
          >
            {t`web.member.serviceRequest.edit`}
          </Button>
        )}
        {serviceRequest.status !== INTERACTION_CANCELLED && !isEditing && (
          <WhiteLabelButton
            onClick={() => setOpenCancelRequestModel(true)}
            testId="cancel-button"
            variant="secondary"
            className={styles.cancelRequestButton}
          >
            {t`web.member.serviceRequest.cancel.request`}
          </WhiteLabelButton>
        )}
      </div>
    </div>
  );
};

const ServiceRequestDetails = ({
  serviceRequest,
  isPropertyChannel,
  timeZone,
}: {
  serviceRequest: ServiceRequestData;
  isPropertyChannel: boolean;
  timeZone: string;
}) => {
  const { t } = useTranslation();
  const isScheduleServiceRequestDueDateEnabled =
    useScheduleServiceRequestDueDateEnabled();

  return (
    <div className={styles.workOrderDataContainer}>
      <div className={styles.section}>
        <div
          className={styles.fieldSection}
          data-test="service-request-category"
        >
          <p>{t`web.member.serviceRequest.category`}</p>
          <span>{serviceRequest?.category}</span>
        </div>

        <div className={styles.fieldSection} data-test="service-request-issue">
          <p>{t`web.admin.serviceRequest.issue`}</p>
          <span>{serviceRequest?.issue}</span>
        </div>

        <div
          className={styles.fieldSection}
          data-test="service-request-description"
        >
          <p>{t`web.admin.serviceRequest.description`}</p>
          <span>{serviceRequest?.description}</span>
        </div>

        <div
          className={styles.fieldSection}
          data-test="service-request-location"
        >
          <p>{t`web.member.serviceRequest.issueLocation`}</p>
          <span>{serviceRequest?.location}</span>
        </div>

        {isScheduleServiceRequestDueDateEnabled && (
          <div className={styles.fieldSection}>
            <p>{t`web.member.serviceRequest.dateRequested`}</p>
            {serviceRequest?.scheduledDueDate ? (
              <span>
                {dateFormatter(
                  serviceRequest?.scheduledDueDate,
                  LONG_DATE_TIME,
                  timeZone
                )}
              </span>
            ) : (
              <span>{EMPTY_VALUE}</span>
            )}
          </div>
        )}
        {isPropertyChannel && (
          <>
            {serviceRequest?.floor && (
              <div className={styles.fieldSection}>
                <p>{t`web.admin.serviceRequest.floor`}</p>
                <span>{serviceRequest?.floor}</span>
              </div>
            )}
            {serviceRequest?.suite && (
              <div className={styles.fieldSection}>
                <p>{t`web.admin.serviceRequest.suite`} </p>
                <span>{serviceRequest?.suite}</span>
              </div>
            )}
          </>
        )}
        <div className={styles.fieldSection}>
          {serviceRequest.customQuestions &&
            serviceRequest.customQuestions
              .filter(
                (question): question is CustomQuestion => question !== null
              )
              .map((customQuestion: CustomQuestion, index: number) => (
                <div className={styles.fieldSection} key={index}>
                  <p>{customQuestion.Question}</p>
                  <span>{customQuestion.Answer}</span>
                </div>
              ))}
        </div>
      </div>
    </div>
  );
};

const AttachmentsSection = ({
  attachmentRefId,
  serviceRequestFeatures,
  hideAttachmentLabel,
  setHideAttachmentLabel,
}: {
  attachmentRefId: string;
  serviceRequestFeatures: ChannelServiceRequestFeaturesType;
  hideAttachmentLabel: boolean;
  setHideAttachmentLabel: (hide: boolean) => void;
}) => {
  const { t } = useTranslation();

  return (
    <Flex direction="column" className={styles.fieldSection} width="full">
      {!hideAttachmentLabel && (
        <p>{t`web.admin.serviceRequest.attachment.heading`}</p>
      )}
      {serviceRequestFeatures.updateServiceRequest && (
        <span>{t`web.admin.serviceRequest.attachment.disclaimer`}</span>
      )}
      <Flex className={styles.attachmentsField} direction="column" width="full">
        <AddAttachment
          entityId={attachmentRefId}
          entityType={ContentTypeEnum.WorkOrder}
          editMode={false}
          variant={AttachmentVariantEnum.WorkOrder}
          canAddAttachments={serviceRequestFeatures.updateServiceRequest}
          hideAttachmentLabel={setHideAttachmentLabel}
          preventDelete={!serviceRequestFeatures.updateServiceRequest}
          acceptedFileTypes={[
            ...Object.values(AttachmentImageContentTypeEnum),
            AttachmentDocumentContentTypeEnum.pdf,
          ]}
          s3Bucket={AmazonS3Buckets.Activate}
        />
      </Flex>
    </Flex>
  );
};

const UserInformation = ({
  serviceRequest,
}: {
  serviceRequest: ServiceRequestData;
}) => {
  const { t } = useTranslation();

  return (
    <div className={styles.userContainer}>
      <div className={styles.fieldSection}>
        <p>{t`web.admin.serviceRequest.submittedBy`}</p>
        <UserThumbnailWithLoader
          id={serviceRequest.createdBy?.id || undefined}
          className={styles.user}
          labelPosition="right"
          splitName={false}
        />
      </div>

      {serviceRequest?.assignee && (
        <div className={styles.fieldSection}>
          <p>{t`web.admin.serviceRequest.assignedTo`}</p>
          <UserThumbnailWithLoader
            className={styles.user}
            id={serviceRequest?.assignee?.id || undefined}
            labelPosition="right"
            splitName={false}
          />
        </div>
      )}
    </div>
  );
};

const CancelRequestModal = ({
  openCancelRequestModel,
  setOpenCancelRequestModel,
  cancelRequest,
}: {
  openCancelRequestModel: boolean;
  setOpenCancelRequestModel: (open: boolean) => void;
  cancelRequest: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      isOpen={openCancelRequestModel}
      onClose={() => setOpenCancelRequestModel(false)}
      size="large"
      closeButtonSize="small"
      title={t`web.member.serviceRequest.cancel.modal.action.header`}
      actions={
        <Button
          variant="contained"
          size="medium"
          onClick={cancelRequest}
          className={styles.cancelButton}
          testId="confirm-cancel-button"
        >
          {t`web.member.serviceRequest.cancel.modal.action.confirm`}
        </Button>
      }
    >
      <M
        variant="secondary"
        mt={3}
      >{t`web.member.serviceRequest.cancel.modal.action.message`}</M>
    </Modal>
  );
};

const WorkOrderCommentsSection = ({
  isWorkOrderCommentsEnabled,
  messagingToken,
  serviceRequest,
  userId,
  userName,
}: {
  isWorkOrderCommentsEnabled: boolean;
  messagingToken: string | null | undefined;
  serviceRequest: ServiceRequestData;
  userId: string | undefined;
  userName: string | undefined;
}) => {
  const { t } = useTranslation();

  return isWorkOrderCommentsEnabled &&
    messagingToken &&
    userId &&
    serviceRequest?.serviceRequestId ? (
    <div className={cx(styles.divider, 'my-2')}>
      <WorkOrderComments
        channelId={convertToUUID(serviceRequest.serviceRequestId)}
        chatTitle={t`web.member.serviceRequest.comments.title`}
        commentBoxPosition="top"
        messagingToken={messagingToken}
        user={{ id: convertToUUID(userId), name: userName }}
      />
    </div>
  ) : null;
};

const ServiceRequestContent = ({
  serviceRequest,
  canEdit,
  isEditing,
  setIsEditing,
  isPropertyChannel,
  serviceRequestFeatures,
  hideAttachmentLabel,
  setHideAttachmentLabel,
  isWorkOrderCommentsEnabled,
  messagingToken,
  userId,
  userName,
  settings,
  refetch,
  channel,
  timeZone,
  onFeedbackSubmit,
  setOpenCancelRequestModel,
}: {
  serviceRequest: ServiceRequestData;
  canEdit: boolean;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  isPropertyChannel: boolean;
  serviceRequestFeatures: ChannelServiceRequestFeaturesType;
  hideAttachmentLabel: boolean;
  setHideAttachmentLabel: (hide: boolean) => void;
  isWorkOrderCommentsEnabled: boolean;
  messagingToken: string | null | undefined;
  userId: string | undefined;
  userName: string | undefined;
  settings: WorkOrderModuleSettings;
  refetch: () => void;
  channel: Channel;
  timeZone: string;
  onFeedbackSubmit: () => void;
  setOpenCancelRequestModel: (open: boolean) => void;
}) => {
  const { t } = useTranslation();

  if (isEditing) {
    return (
      <MemberServiceRequestEdit
        close={() => setIsEditing(false)}
        settings={settings}
        serviceRequest={serviceRequest}
        refetch={refetch}
      />
    );
  }

  return (
    <>
      <ServiceRequestHeader
        serviceRequest={serviceRequest}
        canEdit={canEdit}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        onFeedbackSubmit={onFeedbackSubmit}
        settings={settings}
        setOpenCancelRequestModel={setOpenCancelRequestModel}
        timeZone={timeZone}
      />
      <div className={styles.fieldSection}>
        <p>{t`web.member.serviceRequest.request`}</p>
        <span>{serviceRequest?.userFriendlyID}</span>
      </div>
      <div className={styles.statusChipContainer}>
        <StatusChip
          status={serviceRequest.status || undefined}
          className={styles.statusChip}
        />
      </div>

      <ServiceRequestDetails
        serviceRequest={serviceRequest}
        isPropertyChannel={isPropertyChannel}
        timeZone={timeZone}
      />

      <div className={cx(styles.divider)}>
        {serviceRequest.attachmentRefId && (
          <AttachmentsSection
            attachmentRefId={serviceRequest.attachmentRefId}
            serviceRequestFeatures={serviceRequestFeatures}
            hideAttachmentLabel={hideAttachmentLabel}
            setHideAttachmentLabel={setHideAttachmentLabel}
          />
        )}
        <UserInformation serviceRequest={serviceRequest} />
      </div>

      <MemberChargeList
        channel={channel}
        externalEntityType={ExternalEntityType.EXTERNAL_ENTITY_TYPE_WORKORDER}
        externalEntityId={convertToUUID(serviceRequest.serviceRequestId)}
      />

      <WorkOrderCommentsSection
        isWorkOrderCommentsEnabled={isWorkOrderCommentsEnabled}
        messagingToken={messagingToken}
        serviceRequest={serviceRequest}
        userId={userId}
        userName={userName}
      />
    </>
  );
};

const CancelRequestSection = ({
  openCancelRequestModel,
  setOpenCancelRequestModel,
  cancelRequest,
}: {
  openCancelRequestModel: boolean;
  setOpenCancelRequestModel: (open: boolean) => void;
  cancelRequest: () => void;
}) => {
  return (
    <div className={cx(styles.divider)}>
      <CancelRequestModal
        openCancelRequestModel={openCancelRequestModel}
        setOpenCancelRequestModel={setOpenCancelRequestModel}
        cancelRequest={cancelRequest}
      />
    </div>
  );
};

const useServiceRequestData = (serviceRequestId: string, channel: Channel) => {
  const { t } = useTranslation();
  const { data, refetch } = useQuery(getServiceRequestByIdQuery, {
    variables: {
      serviceRequestId,
    },
  });

  const { channel: channelProfile } = useChannelProfileQuery({
    channelId: channel?._id,
  });

  const { data: messagingTokenData } = useQuery(getMyMessagingTokenQuery);

  const messagingToken = messagingTokenData?.me?.user?.messagingToken;
  const userId = messagingTokenData?.me?.user?._id;
  const userName = messagingTokenData?.me?.user?.name;

  const serviceRequest = data?.serviceRequest;
  const canEdit = serviceRequest?.status !== INTERACTION_CLOSED || false;
  const [isEditing, setIsEditing] = useState(false);
  const [openCancelRequestModel, setOpenCancelRequestModel] = useState(false);
  const [serviceRequestFeatures, setServiceRequestFeatures] =
    useState<ChannelServiceRequestFeaturesType>({
      createServiceRequest: true,
      updateServiceRequest: true,
    });

  const [hideAttachmentLabel, setHideAttachmentLabel] = useState(false);

  useChannelServiceRequestFeaturesQuery(channel, setServiceRequestFeatures);

  useSetContextualLDAttributes({
    activeContentOriginChannelId: serviceRequest?.channelId?.id
      ? convertTo62(serviceRequest.channelId.id)
      : undefined,
  });

  const isWorkOrderCommentsEnabled = useFlag(
    FeatureFlag.WorkOrderComments,
    false
  );

  const isPropertyChannel =
    channelProfile?.type === ActiveChannelTypeEnum.Property;

  useEffect(() => {
    setIsEditing(false);
  }, [serviceRequestId]);

  return {
    t,
    serviceRequest,
    canEdit,
    isEditing,
    setIsEditing,
    openCancelRequestModel,
    setOpenCancelRequestModel,
    serviceRequestFeatures,
    hideAttachmentLabel,
    setHideAttachmentLabel,
    isWorkOrderCommentsEnabled,
    messagingToken,
    userId,
    userName,
    isPropertyChannel,
    refetch,
  };
};

export function MemberServiceRequestDetail({
  serviceRequestId,
  settings,
  channel,
  timeZone,
}: MemberServiceRequestDetailProps) {
  const location = useLocation();
  const isTenantSurveysEnabled = useWorkOrderTenantSurveysEnabled();

  // Extract contentId from URL path using route matching
  const match = useRouteMatch<{ id: string }>({
    path: routes.content,
    strict: true,
    sensitive: true,
  });

  // ContentId should always be present since this component is only rendered on content routes
  const contentId = match?.params.id;

  const {
    t,
    serviceRequest,
    canEdit,
    isEditing,
    setIsEditing,
    openCancelRequestModel,
    setOpenCancelRequestModel,
    serviceRequestFeatures,
    hideAttachmentLabel,
    setHideAttachmentLabel,
    isWorkOrderCommentsEnabled,
    messagingToken,
    userId,
    userName,
    isPropertyChannel,
    refetch,
  } = useServiceRequestData(serviceRequestId, channel);

  const serviceRequestStatus = getServiceRequestStatusEnum(
    serviceRequest?.status
  );
  const { isSurveySubmissionEligible: canShowFeedback } =
    useSurveySubmissionEligibility(settings, serviceRequestStatus);

  const currentUrlParams = new URLSearchParams(location.search);
  const urlServiceRequestId = currentUrlParams.get('serviceRequestId');

  const shouldShowFeedback =
    currentUrlParams.has('feedback') &&
    urlServiceRequestId === serviceRequestId &&
    !!serviceRequestId &&
    contentId &&
    serviceRequest &&
    canShowFeedback;

  const cancelRequest = async () => {
    try {
      await getClient().mutate({
        mutation: updateServiceRequestStatusByIdMutation,
        variables: {
          id: safeConvertToUUID(serviceRequestId),
          status: ServiceRequestStatusEnum.Cancelled,
        },
      });
      window.Toast.show(t('web.member.serviceRequest.update.success'));
      setOpenCancelRequestModel(false);
      window.location.reload();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      window.Toast.show(t('web.member.serviceRequest.update.failure'));
    }
  };

  const handleFeedbackSubmit = () => {
    refetch();
  };

  if (!serviceRequest) {
    return null;
  }

  return (
    <div>
      <ServiceRequestContent
        serviceRequest={serviceRequest}
        canEdit={canEdit}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        isPropertyChannel={isPropertyChannel}
        serviceRequestFeatures={serviceRequestFeatures}
        hideAttachmentLabel={hideAttachmentLabel}
        setHideAttachmentLabel={setHideAttachmentLabel}
        isWorkOrderCommentsEnabled={isWorkOrderCommentsEnabled || false}
        messagingToken={messagingToken}
        userId={userId}
        userName={userName}
        settings={settings}
        refetch={refetch}
        channel={channel}
        onFeedbackSubmit={handleFeedbackSubmit}
        setOpenCancelRequestModel={setOpenCancelRequestModel}
        timeZone={timeZone}
      />

      <CancelRequestSection
        openCancelRequestModel={openCancelRequestModel}
        setOpenCancelRequestModel={setOpenCancelRequestModel}
        cancelRequest={cancelRequest}
      />

      {isTenantSurveysEnabled && shouldShowFeedback && (
        <ServiceRequestFeedbackContainer
          serviceRequest={{
            serviceRequestId: serviceRequest.serviceRequestId,
            userFriendlyID: serviceRequest.userFriendlyID || '',
            status: serviceRequest.status || '',
            category: serviceRequest.category || undefined,
            issue: serviceRequest.issue || undefined,
          }}
          contentId={contentId}
          feedback={serviceRequest.feedback || undefined}
          onFeedbackSubmit={handleFeedbackSubmit}
        />
      )}
    </div>
  );
}
